import { useEffect, useState } from 'react';
import { Button, Form, InputGroup, Stack } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import OtpInput from 'react-otp-input';
import { useHistory, useParams } from 'react-router-dom';

import { faEye, faEyeSlash } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  APP_EVENTS,
  captureAppEvent,
  PASSWORD_RESET_VIA,
} from 'src/services/posthogEvents';

import {
  CustomModalBody,
  CustomModalFooter,
} from 'src/components/custom_components/CustomModal';

import { Api } from 'src/models/Api.model';
import { I18NextNS } from 'src/models/Language.model';

import useHandleHttpRequestError from 'src/hooks/useHandleHttpRequestError';
import useValidatePassword from 'src/hooks/useValidatePassword';

import LoginContainer from './LoginContainer';
import { ResetPasswordFormData } from './ResetPasswordForm';

const ResetPasswordWithOTP = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation(I18NextNS.PAGES);
  const history = useHistory();
  const { showHttpRequestError } = useHandleHttpRequestError();

  const [isShowPassword, setIsShowPassword] = useState(false);
  const [isShowConfirmPassword, setIsShowConfirmPassword] = useState(false);

  const { email } = useParams<{ email: string }>();

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
    watch,
    control,
  } = useForm<ResetPasswordFormData>({
    defaultValues: {
      otp: '',
      password: '',
      confirmPassword: '',
    },
    criteriaMode: 'all',
  });

  const password = watch('password');

  const handleResetPassword = async (data: ResetPasswordFormData) => {
    try {
      await Api.remote.resetPassword(
        email,
        data.otp,
        data.password,
        data.confirmPassword,
      );

      captureAppEvent(APP_EVENTS.PASSWORD_RESET, {
        via: PASSWORD_RESET_VIA.FORGOT,
      });

      Api.alertBox({
        title: t('bootbox.title.Success', {
          ns: I18NextNS.COMMON,
        }),
        message: t('changePassword.resetPasswordSuccessfully', {
          ns: I18NextNS.PAGES,
        }),
        locale: language,
        type: 'success',
        callback: () => {
          history.replace('/');
        },
      });
    } catch (error) {
      showHttpRequestError(error);
    }
  };

  const [countdownSeconds, setCountdownSeconds] = useState(30);

  useEffect(() => {
    if (countdownSeconds > 0) {
      const intervalId = setInterval(() => {
        setTimeout(() => {
          setCountdownSeconds((prevSeconds) => prevSeconds - 1);
        }, 500);
      }, 1000);

      return () => clearInterval(intervalId);
    }
  }, [countdownSeconds]);

  const handleResendCode = async () => {
    try {
      await Api.remote.sendResetCode(email);
      setCountdownSeconds(30);
    } catch (err) {
      showHttpRequestError(err);
    }
  };

  return (
    <LoginContainer>
      <Form onSubmit={handleSubmit(handleResetPassword)}>
        <CustomModalBody>
          <Stack className="text-center">
            <h2 data-test="reset-password-page-title">
              {t('changePassword.title')}
            </h2>
            <p className="mb-0" data-test="reset-password-otp-instructions">
              <small>{t('changePassword.sendOTPCode')}</small>
            </p>
            <small>
              {
                <Stack
                  direction="horizontal"
                  className="justify-content-center"
                  gap={1}
                >
                  <span data-test="reset-password-did-not-receive-code-text">
                    {t('loginLogout.didNotReceiveCode')}
                  </span>
                  {countdownSeconds > 0 ? (
                    <Trans
                      i18nKey="loginLogout.resendCodeInSeconds"
                      ns="pages"
                      values={{ countdownSeconds }}
                      components={[
                        <strong data-test="reset-password-countdown-seconds">
                          {countdownSeconds}
                        </strong>,
                      ]}
                    />
                  ) : (
                    <Button
                      variant="link"
                      className="p-0 text-primary"
                      size="sm"
                      onClick={handleResendCode}
                      data-test="reset-password-resend-code-link"
                      data-track="auth.reset-password-otp.resend-code"
                      data-observe
                    >
                      {t('loginLogout.resendCode')}
                    </Button>
                  )}
                </Stack>
              }
            </small>
          </Stack>

          <Form.Group
            className="mb-2"
            data-test="reset-password-otp-code-section"
          >
            <Form.Label>
              <strong>{t('changePassword.otpCode')}</strong>
            </Form.Label>

            <Controller
              name="otp"
              control={control}
              defaultValue=""
              rules={{ required: t('changePassword.otpCodeRequired') }}
              render={({ field }) => (
                <OtpInput
                  inputType="tel"
                  containerStyle={{ display: 'flex', justifyContent: 'center' }}
                  numInputs={6}
                  renderSeparator={<span className="mx-2">-</span>}
                  inputStyle={{
                    width: '3rem',
                    height: '3rem',
                    borderRadius: '0.25rem',
                    border: '1px solid #ced4da',
                    textAlign: 'center',
                    fontSize: '1.5rem',
                  }}
                  renderInput={(props) => <input {...props} />}
                  shouldAutoFocus
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
            <div className="text-danger" data-test="reset-password-otp-error">
              {errors.otp?.message}
            </div>
          </Form.Group>

          <Form.Group className="mb-2">
            <Form.Label>
              <strong>{t('changePassword.newPassword')}</strong>
            </Form.Label>
            <InputGroup>
              <Form.Control
                type={isShowPassword ? 'text' : 'password'}
                placeholder={t('changePassword.newPasswordPlaceholder')}
                autoComplete="off"
                data-test="reset-password-new-password-input"
                {...register('password', {
                  required: t('changePassword.passwordRequired'),
                  validate: useValidatePassword(),
                })}
              />
              <InputGroup.Text
                onClick={() => setIsShowPassword(!isShowPassword)}
                className="pointer"
              >
                <FontAwesomeIcon icon={isShowPassword ? faEye : faEyeSlash} />
              </InputGroup.Text>
            </InputGroup>
            <div
              className="text-danger"
              data-test="reset-password-new-password-error"
            >
              {errors.password?.type === 'required' ? (
                <p>{t('changePassword.newPasswordRequired')}</p>
              ) : (
                <ul className="list-unstyled">
                  {errors.password?.types?.minLength && (
                    <li>{errors.password.message}</li>
                  )}
                  {errors.password?.types?.hasUpperCase && (
                    <li>{errors.password.types.hasUpperCase}</li>
                  )}
                  {errors.password?.types?.hasLowerCase && (
                    <li>{errors.password.types.hasLowerCase}</li>
                  )}
                  {errors.password?.types?.hasNumber && (
                    <li>{errors.password.types.hasNumber}</li>
                  )}
                  {errors.password?.types?.hasSpecialChar && (
                    <li>{errors.password.types.hasSpecialChar}</li>
                  )}
                </ul>
              )}
            </div>
          </Form.Group>

          <Form.Group className="mb-2">
            <Form.Label>
              <strong>{t('changePassword.confirmPassword')}</strong>
            </Form.Label>
            <InputGroup>
              <Form.Control
                type={isShowConfirmPassword ? 'text' : 'password'}
                placeholder={t('changePassword.confirmPasswordPlaceholder')}
                autoComplete="off"
                data-test="reset-password-confirm-password-input"
                {...register('confirmPassword', {
                  required: t('changePassword.confirmPasswordRequired'),
                  validate: (value) =>
                    value === password ||
                    t('changePassword.passwordsDoNotMatch'),
                })}
              />
              <InputGroup.Text
                onClick={() => setIsShowConfirmPassword(!isShowConfirmPassword)}
                className="pointer"
              >
                <FontAwesomeIcon
                  icon={isShowConfirmPassword ? faEye : faEyeSlash}
                />
              </InputGroup.Text>
            </InputGroup>
            {
              <p
                className="text-danger "
                data-test="reset-password-confirm-password-error"
              >
                {errors.confirmPassword?.message}
              </p>
            }
          </Form.Group>
        </CustomModalBody>
        <CustomModalFooter className="justify-content-end">
          <Button
            variant="primary-100"
            onClick={() => {
              history.goBack();
            }}
            data-test="reset-password-back-button"
            data-track="auth.reset-password-otp.back"
            data-observe
          >
            {t('changePassword.back')}
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={isSubmitting}
            data-test="reset-password-continue-button"
            data-track="auth.reset-password-otp.submit"
            data-observe
          >
            {isSubmitting
              ? t('changePassword.submitting')
              : t('changePassword.continue')}
          </Button>
        </CustomModalFooter>
      </Form>
    </LoginContainer>
  );
};

export default ResetPasswordWithOTP;
